import React, { useEffect, useState, useContext, useRef } from 'react';
import {
    Box, Typography, List, ListItem, ListItemButton, ListItemText, ListItemAvatar,
    Avatar, Divider, Paper, CircularProgress, TextField, IconButton,
    Chip, Badge, InputAdornment, Alert, Snackbar, InputBase
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SearchIcon from '@mui/icons-material/Search';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import {
    importPublicKey,
    createAndPackageUserKeys,
    decryptPrivateKey,
    encryptMessageForRecipients,
    decryptMessage
} from '../../utils/cryptoUtil';
import ChatMessageContent from '../../components/chat/ChatMessageContent';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Contact {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    publicKey?: string;
    role?: string;
    preferences?: { photoUrl?: string };
}

interface EncryptedMessage {
    _id: string;
    sender: { _id: string; firstName: string; lastName: string; preferences?: { photoUrl?: string } };
    encryptedContent: string;
    accessKeys: { userId: string; encryptedDEK: string }[];
    readBy: string[];
    createdAt: string;
}

interface DecryptedMessage {
    _id: string;
    sender: EncryptedMessage['sender'];
    content: string;
    createdAt: string;
    isOwn: boolean;
    decryptionFailed?: boolean;
    isPartiallyEncrypted?: boolean;
}

// ── Crypto helpers (same as ChatPage) ─────────────────────────────────────────

// Standard crypto helpers are now imported from cryptoUtil.ts

// ── Component ─────────────────────────────────────────────────────────────────

const AdminChatPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const [learners, setLearners] = useState<Contact[]>([]);
    const [trainers, setTrainers] = useState<Contact[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
    const [encryptedMessages, setEncryptedMessages] = useState<EncryptedMessage[]>([]);
    const [decryptedMessages, setDecryptedMessages] = useState<DecryptedMessage[]>([]);
    const [messageInput, setMessageInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [decrypting, setDecrypting] = useState(false);
    const [e2eeReady, setE2eeReady] = useState(false);
    const [needsKeySetup, setNeedsKeySetup] = useState(false);
    const [keySetupPassword, setKeySetupPassword] = useState('');
    const [setupLoading, setSetupLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
    const [masterPublicKey, setMasterPublicKey] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const show = (message: string, severity: 'success' | 'error' | 'info' = 'info') =>
        setSnackbar({ open: true, message, severity });

    // E2EE key state
    useEffect(() => {
        if (auth?.privateKey) {
            setE2eeReady(true);
        } else if (auth?.user) {
            setNeedsKeySetup(true);
        }
    }, [auth?.privateKey, auth?.user]);

    // Fetch admin contacts
    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await api.get('/api/chat/admin-contacts');
                setLearners(res.data.learners || []);
                setTrainers(res.data.trainers || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        if (auth?.user) fetch();
    }, [auth?.user]);

    // Fetch unread counts per contact
    const fetchUnread = async () => {
        try {
            const res = await api.get('/api/chat/unread-by-contact');
            setUnreadCounts(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (auth?.user) fetchUnread();
    }, [auth?.user]);

    // Mark messages as read when contact is selected
    useEffect(() => {
        if (selectedContact) {
            api.patch(`/api/chat/read-all/${selectedContact._id}`)
                .then(() => {
                    setUnreadCounts(prev => {
                        const newCounts = { ...prev };
                        delete newCounts[selectedContact._id];
                        return newCounts;
                    });
                })
                .catch(console.error);
        }
    }, [selectedContact]);

    // Fetch MasterKey for audit
    useEffect(() => {
        if (auth?.user?.organizationId) {
            const orgId = typeof auth.user.organizationId === 'object'
                ? (auth.user.organizationId as any)?._id
                : auth.user.organizationId;
            api.get(`/api/organizations/public/master-key/${orgId}`)
                .then(res => {
                    if (res.data.isMasterKeySet) setMasterPublicKey(res.data.masterPublicKey);
                })
                .catch(console.error);
        }
    }, [auth?.user?.organizationId]);

    // Load messages for selected contact
    useEffect(() => {
        if (!selectedContact) return;
        setDecrypting(true);
        setEncryptedMessages([]); // Clear previous messages immediately
        api.get(`/api/chat/messages/${selectedContact._id}`)
            .then(r => setEncryptedMessages(r.data))
            .catch(console.error)
            .finally(() => setDecrypting(false));
    }, [selectedContact]);

    // Decrypt messages when E2EE key & encrypted list change
    useEffect(() => {
        if (!auth?.privateKey || encryptedMessages.length === 0) {
            setDecryptedMessages([]); // Always clear if no messages or no key
            return;
        }
        const decrypt = async () => {
            setDecrypting(true);
            const result: DecryptedMessage[] = [];
            for (const msg of encryptedMessages) {
                const isOwn = msg.sender._id === auth?.user?._id;
                try {
                    const myKey = msg.accessKeys.find(k => k.userId === auth?.user?._id);
                    if (!myKey) {
                        result.push({
                            _id: msg._id,
                            sender: msg.sender,
                            content: '[Clé manquante]',
                            createdAt: msg.createdAt,
                            isOwn,
                            decryptionFailed: true
                        });
                        continue;
                    }

                    const content = await decryptMessage(msg.encryptedContent, myKey.encryptedDEK, auth.privateKey!);

                    // Check for partial encryption
                    let isPartiallyEncrypted = false;
                    if (isOwn) {
                        // Recipient must have a key
                        if (selectedContact && !msg.accessKeys.some(k => k.userId === selectedContact._id)) {
                            isPartiallyEncrypted = true;
                        }
                        // Auditor must have a key if masterPublicKey is set
                        if (masterPublicKey) {
                            const orgId = typeof auth?.user?.organizationId === 'object'
                                ? (auth?.user?.organizationId as any)?._id
                                : auth?.user?.organizationId;
                            if (orgId && !msg.accessKeys.some(k => k.userId === orgId)) {
                                isPartiallyEncrypted = true;
                            }
                        }
                    }

                    result.push({
                        _id: msg._id,
                        sender: msg.sender,
                        content,
                        createdAt: msg.createdAt,
                        isOwn,
                        isPartiallyEncrypted
                    });
                } catch (e) {
                    console.error('Decryption error:', e);
                    result.push({
                        _id: msg._id,
                        sender: msg.sender,
                        content: '',
                        createdAt: msg.createdAt,
                        isOwn,
                        decryptionFailed: true
                    });
                }
            }
            setDecryptedMessages(result);
            setDecrypting(false);
        };

        decrypt();
    }, [encryptedMessages, auth?.privateKey, auth?.user?._id]);

    const handleResend = (content: string) => {
        if (!content) {
            show("Contenu du message perdu, impossible de renvoyer.", "error");
            return;
        }
        setMessageInput(content);
        const inputElement = document.getElementById('chat-input-admin');
        if (inputElement) inputElement.focus();
        show("Message copié dans le champ de saisie pour renvoi.", "info");
    };

    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [decryptedMessages]);

    const handleSend = async () => {
        if (!messageInput.trim() || !selectedContact || !auth?.privateKey || !auth?.user) return;
        setSending(true);
        try {
            const recipientKeyRes = await api.get(`/api/chat/public-key/${selectedContact._id}`);
            if (!recipientKeyRes.data.publicKey) { show("Ce contact n'a pas encore de clé E2EE", 'error'); return; }

            const recipients = [
                { userId: auth.user._id, publicKey: auth.user.publicKey! },
                { userId: selectedContact._id, publicKey: recipientKeyRes.data.publicKey }
            ];

            if (masterPublicKey) {
                recipients.push({ userId: auth.user.organizationId as any, publicKey: masterPublicKey });
            }

            const { encryptedContent, accessKeys } = await encryptMessageForRecipients(messageInput.trim(), recipients);
            const res = await api.post('/api/chat/messages', { recipientId: selectedContact._id, encryptedContent, accessKeys });

            setDecryptedMessages(p => [...p, {
                _id: res.data._id,
                sender: { _id: auth.user!._id, firstName: auth.user!.firstName, lastName: auth.user!.lastName },
                content: messageInput.trim(), createdAt: new Date().toISOString(), isOwn: true
            }]);
            setEncryptedMessages(p => [...p, res.data]);
            setMessageInput('');
        } catch (err: any) {
            show(err?.response?.data?.message || 'Erreur lors de l\'envoi', 'error');
        } finally { setSending(false); }
    };

    const handleSetupKeys = async () => {
        if (!keySetupPassword || !auth?.user) return;
        setSetupLoading(true);
        try {
            if (auth.user.encryptedPrivateKey) {
                const key = await decryptPrivateKey(auth.user.encryptedPrivateKey, keySetupPassword);
                auth.setPrivateKey(key);
            } else {
                const keys = await createAndPackageUserKeys(keySetupPassword);
                await api.put('/api/chat/register-keys', keys);
                auth.updateUser({ ...auth.user, publicKey: keys.publicKey, encryptedPrivateKey: keys.encryptedPrivateKey } as any);
                const key = await decryptPrivateKey(keys.encryptedPrivateKey, keySetupPassword);
                auth.setPrivateKey(key);
            }
            setNeedsKeySetup(false);
            setE2eeReady(true);
            show('Messagerie E2EE activée ✅', 'success');
        } catch { show('Mot de passe incorrect', 'error'); }
        finally { setSetupLoading(false); setKeySetupPassword(''); }
    };

    const fmt = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const fmtDate = (iso: string) => {
        const d = new Date(iso), t = new Date();
        if (d.toDateString() === t.toDateString()) return "Aujourd'hui";
        const y = new Date(t); y.setDate(t.getDate() - 1);
        if (d.toDateString() === y.toDateString()) return 'Hier';
        return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    };
    const grouped = decryptedMessages.reduce<Record<string, DecryptedMessage[]>>((acc, m) => {
        const k = fmtDate(m.createdAt); if (!acc[k]) acc[k] = []; acc[k].push(m); return acc;
    }, {});

    const filterContacts = (list: Contact[]) =>
        list.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()));

    // ── Key setup panel ──────────────────────────────────────────────────────────
    if (needsKeySetup && !e2eeReady) return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Paper elevation={0} sx={{ p: 4, maxWidth: 440, width: '100%', textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <Box sx={{ width: 68, height: 68, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <VpnKeyIcon sx={{ fontSize: 34, color: 'white' }} />
                </Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                    {auth?.user?.encryptedPrivateKey ? 'Déverrouiller la messagerie' : 'Activer la messagerie E2EE'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Entrez votre mot de passe pour {auth?.user?.encryptedPrivateKey ? 'accéder à vos messages sécurisés.' : 'générer votre clé de chiffrement.'}
                </Typography>
                <Alert severity="info" sx={{ mb: 2, textAlign: 'left', fontSize: '0.8rem' }}>
                    <strong>Zero-knowledge</strong> — votre clé privée ne quitte jamais votre navigateur.
                </Alert>
                <TextField type="password" fullWidth label="Mot de passe" value={keySetupPassword}
                    onChange={e => setKeySetupPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSetupKeys(); }}
                    sx={{ mb: 2 }} disabled={setupLoading} />
                <Box onClick={setupLoading ? undefined : handleSetupKeys}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, cursor: 'pointer', py: 1.5, px: 3, borderRadius: 2, background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', fontWeight: 600 }}>
                    {setupLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <LockOpenIcon />}
                    {auth?.user?.encryptedPrivateKey ? 'Déverrouiller' : 'Générer mes clés'}
                </Box>
            </Paper>
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );

    // ── Full chat interface ───────────────────────────────────────────────────────
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2 }, gap: 0 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" fontWeight={700}>Messagerie</Typography>
                <Chip
                    icon={e2eeReady ? <LockIcon sx={{ fontSize: '14px !important' }} /> : <LockOpenIcon sx={{ fontSize: '14px !important' }} />}
                    label={e2eeReady ? 'E2EE Actif' : 'Non sécurisé'}
                    size="small" color={e2eeReady ? 'success' : 'warning'} sx={{ fontSize: '0.7rem' }}
                />
            </Box>

            <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1.5, pt: 1.5 }}>
                {/* Contacts Sidebar */}
                <Paper elevation={0} sx={{ width: { xs: 240, md: 300 }, flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {/* Search */}
                    <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50' }}>
                        <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                        <InputBase fullWidth placeholder="Rechercher…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ fontSize: '0.875rem' }} />
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={22} /></Box>
                        ) : (
                            <>
                                {/* Learners section */}
                                <Box sx={{ px: 2, py: 1, background: 'linear-gradient(90deg, #667eea18, transparent)' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <SchoolIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                        <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                            Apprenants
                                        </Typography>
                                        <Chip label={learners.length} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 'auto' }} />
                                    </Box>
                                </Box>
                                <List dense disablePadding>
                                    {filterContacts(learners).length === 0
                                        ? <ListItem sx={{ py: 1 }}><ListItemText secondary="Aucun apprenant" /></ListItem>
                                        : filterContacts(learners).map(l => (
                                            <ListItemButton key={l._id} selected={selectedContact?._id === l._id}
                                                onClick={() => setSelectedContact(l)} sx={{ py: 1.5 }}>
                                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                                    <Avatar src={l.preferences?.photoUrl} sx={{ width: 32, height: 32, fontSize: '0.75rem', background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                                                        {l.firstName[0]}{l.lastName[0]}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={`${l.firstName} ${l.lastName}`} primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                                                    secondary={l.publicKey ? undefined : 'Sans clé E2EE'} secondaryTypographyProps={{ variant: 'caption', color: 'warning.main' }} />
                                                {unreadCounts[l._id] > 0 && (
                                                    <Badge badgeContent={unreadCounts[l._id]} color="error" sx={{ mr: 1 }} />
                                                )}
                                            </ListItemButton>
                                        ))}
                                </List>

                                <Divider />

                                {/* Trainers section */}
                                <Box sx={{ px: 2, py: 1, background: 'linear-gradient(90deg, #764ba218, transparent)' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <SupervisorAccountIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                                        <Typography variant="caption" fontWeight={700} color="secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                            Formateurs
                                        </Typography>
                                        <Chip label={trainers.length} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 'auto' }} />
                                    </Box>
                                </Box>
                                <List dense disablePadding>
                                    {filterContacts(trainers).length === 0
                                        ? <ListItem sx={{ py: 1 }}><ListItemText secondary="Aucun formateur" /></ListItem>
                                        : filterContacts(trainers).map(t => (
                                            <ListItemButton key={t._id} selected={selectedContact?._id === t._id}
                                                onClick={() => setSelectedContact(t)} sx={{ py: 1.5 }}>
                                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                                    <Avatar src={t.preferences?.photoUrl} sx={{ width: 32, height: 32, fontSize: '0.75rem', background: 'linear-gradient(135deg, #764ba2, #b06ab3)' }}>
                                                        {t.firstName[0]}{t.lastName[0]}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={`${t.firstName} ${t.lastName}`} primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                                                    secondary={t.publicKey ? undefined : 'Sans clé E2EE'} secondaryTypographyProps={{ variant: 'caption', color: 'warning.main' }} />
                                                {unreadCounts[t._id] > 0 && (
                                                    <Badge badgeContent={unreadCounts[t._id]} color="error" sx={{ mr: 1 }} />
                                                )}
                                            </ListItemButton>
                                        ))}
                                </List>
                            </>
                        )}
                    </Box>
                </Paper>

                {/* Chat Panel */}
                <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    {!selectedContact ? (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, opacity: 0.5 }}>
                            <LockIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h6" fontWeight={600} align="center">Sélectionnez un contact</Typography>
                            <Typography variant="body2" color="text.secondary" align="center">
                                Les messages sont chiffrés de bout en bout —<br />ni le serveur ni l'équipe technique ne peuvent les lire.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Contact header */}
                            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, background: 'linear-gradient(90deg, #667eea08, #764ba208)' }}>
                                <Avatar src={selectedContact.preferences?.photoUrl} sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                                    {selectedContact.firstName[0]}{selectedContact.lastName[0]}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>{selectedContact.firstName} {selectedContact.lastName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{selectedContact.email}</Typography>
                                </Box>
                                <Chip icon={selectedContact.publicKey ? <LockIcon sx={{ fontSize: '12px !important' }} /> : <LockOpenIcon sx={{ fontSize: '12px !important' }} />}
                                    label={selectedContact.publicKey ? 'E2EE' : 'Sans clé'} size="small"
                                    color={selectedContact.publicKey ? 'success' : 'warning'} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            </Box>

                            {/* Messages */}
                            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {decrypting
                                    ? <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} /></Box>
                                    : !e2eeReady
                                        ? <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><Alert severity="warning">Déverrouillez vos clés E2EE pour voir les messages.</Alert></Box>
                                        : decryptedMessages.length === 0
                                            ? <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><Typography variant="body2" color="text.secondary">Aucun message — commencez la conversation !</Typography></Box>
                                            : Object.entries(grouped).map(([date, msgs]) => (
                                                <Box key={date}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
                                                        <Divider sx={{ flex: 1 }} />
                                                        <Typography variant="caption" color="text.disabled" sx={{ px: 1, whiteSpace: 'nowrap' }}>{date}</Typography>
                                                        <Divider sx={{ flex: 1 }} />
                                                    </Box>
                                                    {msgs.map(msg => (
                                                        <Box key={msg._id} sx={{ display: 'flex', justifyContent: msg.isOwn ? 'flex-end' : 'flex-start', mb: 0.5 }}>
                                                            {!msg.isOwn && (
                                                                <Avatar sx={{ width: 26, height: 26, mr: 1, alignSelf: 'flex-end', mb: 0.5, fontSize: '0.65rem', background: 'linear-gradient(135deg, #667eea, #764ba2)', flexShrink: 0 }}>
                                                                    {msg.sender.firstName[0]}{msg.sender.lastName[0]}
                                                                </Avatar>
                                                            )}
                                                            <Box sx={{ maxWidth: '70%' }}>
                                                                <Box sx={{
                                                                    px: 1.5, py: 1,
                                                                    borderRadius: msg.isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                                    background: msg.isOwn ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'background.paper',
                                                                    border: msg.isOwn ? 'none' : '1px solid', borderColor: 'divider',
                                                                    color: msg.isOwn ? 'white' : 'text.primary',
                                                                    boxShadow: msg.isOwn ? '0 2px 8px rgba(102,126,234,0.25)' : 'none'
                                                                }}>
                                                                    <ChatMessageContent 
                                                                        content={msg.content} 
                                                                        isOwn={msg.isOwn} 
                                                                        decryptionFailed={msg.decryptionFailed}
                                                                        isPartiallyEncrypted={msg.isPartiallyEncrypted}
                                                                        onResend={() => handleResend(msg.content)}
                                                                    />
                                                                </Box>
                                                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.3, px: 0.5, textAlign: msg.isOwn ? 'right' : 'left' }}>{fmt(msg.createdAt)}</Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ))
                                }
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Input bar */}
                            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                {!e2eeReady
                                    ? <Alert severity="warning" sx={{ borderRadius: 2 }}>Déverrouillez vos clés E2EE pour envoyer des messages.</Alert>
                                    : !selectedContact.publicKey
                                        ? <Alert severity="warning" sx={{ borderRadius: 2 }}>Ce contact n'a pas encore activé son chat E2EE.</Alert>
                                        : (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                                <TextField fullWidth multiline maxRows={4} size="small"
                                                    placeholder="Message chiffré E2EE…"
                                                    value={messageInput} onChange={e => setMessageInput(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                                                    disabled={sending}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 }, '& textarea': { fontSize: '0.9rem' } }}
                                                    InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize: 15, color: 'success.main' }} /></InputAdornment> }}
                                                />
                                                <IconButton onClick={handleSend} disabled={!messageInput.trim() || sending}
                                                    sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', width: 40, height: 40, '&:hover': { background: 'linear-gradient(135deg, #5a6fd8, #6a3d99)' }, '&.Mui-disabled': { background: '#e0e0e0' } }}>
                                                    {sending ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SendIcon sx={{ fontSize: 17 }} />}
                                                </IconButton>
                                            </Box>
                                        )
                                }
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminChatPage;
